/**
 * Story 053 — small formatting helpers shared by the admin surface.
 */

const RTF =
  typeof Intl !== "undefined" && typeof Intl.RelativeTimeFormat === "function"
    ? new Intl.RelativeTimeFormat("en", { numeric: "auto" })
    : null;

/**
 * Render a timestamp as a coarse relative-time label ("2 hours ago",
 * "3 days ago"). Returns "—" for null/undefined.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (!RTF) return new Date(iso).toLocaleString();
  if (abs < 60) return RTF.format(Math.round(diffSec), "second");
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return RTF.format(diffMin, "minute");
  const diffHr = Math.round(diffSec / 3600);
  if (Math.abs(diffHr) < 24) return RTF.format(diffHr, "hour");
  const diffDay = Math.round(diffSec / 86_400);
  if (Math.abs(diffDay) < 30) return RTF.format(diffDay, "day");
  const diffMo = Math.round(diffSec / (86_400 * 30));
  if (Math.abs(diffMo) < 12) return RTF.format(diffMo, "month");
  const diffYr = Math.round(diffSec / (86_400 * 365));
  return RTF.format(diffYr, "year");
}

// Canonical 7-role enum landed in auth/v1@4.3.0 (Story 054a / BE-058).
// Title-case rendering for every wire value. `analyst` is no longer a
// valid contract value but kept in the map for historical audit-log
// rows ("user.role_changed" detail that pre-dates the migration); new
// rows always carry the canonical role_code.
const ROLE_LABELS: Record<string, string> = {
  platform_admin: "Platform Admin",
  tenant_admin: "Tenant Admin",
  project_manager: "Project Manager",
  back_office: "Back Office",
  pre_screener: "Pre-Screener",
  expert_analyst: "Expert Analyst",
  pilot: "Pilot",
  // Legacy — audit-log rows pre-dating the 058 remigration may still
  // reference this. New surfaces never emit it.
  analyst: "Analyst (legacy)",
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * Returns true if the value is one of the known role enum strings. Used
 * by the audit-log formatter to decide whether to humanise a from/to
 * pair as roles ("Role: Analyst → Project Manager") rather than as raw
 * strings.
 */
function isKnownRole(v: unknown): v is string {
  return (
    typeof v === "string" &&
    Object.prototype.hasOwnProperty.call(ROLE_LABELS, v)
  );
}

const ACTION_LABELS: Record<string, string> = {
  "user.created": "User invited",
  "user.role_changed": "Role changed",
  "user.blocked": "Access blocked",
  "user.unblocked": "Access restored",
  "user.removed": "User removed",
  "user.force_password_reset": "Password reset forced",
  "user.password_reset": "Password reset",
  "user.email_change_requested": "Email change requested",
  "user.phone_change_requested": "Phone change requested",
  "user.mfa_factor_added": "MFA factor added",
  "user.mfa_factor_removed": "MFA factor removed",
  "user.session_terminated": "Session terminated",
  "invitation.created": "Invitation sent",
  "invitation.resent": "Invitation re-sent",
  "invitation.revoked": "Invitation revoked",
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function stringify(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

/** Render a JSONB detail blob as a one-line summary. */
export function detailSummary(detail: Record<string, unknown>): string {
  if (detail.from !== undefined && detail.to !== undefined) {
    // Story 054a — when both sides are recognised role enum strings,
    // humanise them and prefix with "Role:" so the audit row reads
    // "Role: Analyst → Project Manager" (AC6). Falls back to the raw
    // from→to rendering for non-role from/to pairs.
    if (isKnownRole(detail.from) && isKnownRole(detail.to)) {
      return `Role: ${roleLabel(detail.from)} → ${roleLabel(detail.to)}`;
    }
    return `${stringify(detail.from)} → ${stringify(detail.to)}`;
  }
  const entries = Object.entries(detail);
  if (entries.length === 0) return "—";
  return entries.map(([k, v]) => `${k}: ${stringify(v)}`).join(", ");
}
